'use client';

import * as Sentry from '@sentry/nextjs';
import { type CustomFormApi, type CustomFormState, Form } from 'informed';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { type RegisterForm, register, type User } from '@/api/auth';
import { Button } from '@/components/buttons';
import { ContinueWithGoogle } from '@/components/ContinueWithGoogle';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { ScFormRegistration } from '@/components/form/groups/styled';
import { InputEmail } from '@/components/form/InputEmail';
import { Password } from '@/components/form/Password';
import { Select } from '@/components/form/Select';
import { NamesSteps } from '@/helpers/auth';
import {
  DEFAULT_CURRENCIES,
  getCurrencyCodeByLocation,
  isCryptoCurrency,
} from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { required, validatePassword } from '@/helpers/validators';
import { useRegistrationCountryOptions } from '@/hooks/common';
import { useLogin } from '@/hooks/login';
import { useNavigation } from '@/hooks/navigation';
import { useSwrMutation } from '@/hooks/swr';
import { useIsLanding } from '@/hooks/useIsLanding';
import { useNDBFlow } from '@/hooks/useNDBFlow';
import { useOrganicStag } from '@/modules/common';
import { PersistentFormTracker } from '@/modules/forms/components/PersistentFormTracker';
import {
  sendRegFormAppearanceEvent,
  sendRegFormFirstTouchedEvent,
  sendRegFormSubmitEvent,
  sendRegGoogleAuthButtonClickEvent,
} from '@/modules/googleAnalytics';
import { useAffiliateRegConfig } from '@/modules/promo';
import { getPromoConsents } from '@/modules/registration/helpers/getPromoConsents';
import { useEmailValidation } from '@/modules/registration/hooks/useEmailValidation';
import { refreshSportsUser } from '@/modules/sports/helpers';
import { useIsSports } from '@/modules/sports/hooks';
import { useGetCommonStore, useModalsStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import {
  PersistentFormName,
  usePersistentFormsSlice,
} from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import { TooltipTypes } from '@/store/tooltipStore';
import { useRegModalTheme } from '../../../../modules/registration/hooks/useRegModalTheme';
import { useErrorValidation } from '../hooks/useErrorValidation';
import { useSyncUserLPCoupon } from '../hooks/useSyncUserLPCoupon';
import { ScRegistrationFormRow, ScRegistrationWrapButtons } from '../styled';
import { RegistrationPromoInput } from './PromoInput';
import { RegistrationFormCheckboxList } from './RegistrationFormCheckboxList';

export interface RegistrationFormProps {
  formId?: string;
  isOneStepRegistration?: boolean;
  openInnerModalsInPortal?: boolean;
  isInputPromoStag?: boolean;
  handleChange?: (formData: CustomFormState<RegisterForm>) => void;
  isSliderShortForm?: boolean;
  oneStepButtonTheme?: Record<string, boolean>;
  oneStepAfterRegUrl?: string;
  regBtnText?: string;
  isFieldsWithInsideLabel?: boolean;
  isOneTermsByDefault?: boolean;
  isSportsPromo?: boolean;
}

export const RegistrationForm: FC<RegistrationFormProps> = ({
  formId,
  isOneStepRegistration: isOneStepRegistrationProp,
  openInnerModalsInPortal,
  isInputPromoStag,
  handleChange,
  isSliderShortForm,
  oneStepButtonTheme = { primary: true },
  oneStepAfterRegUrl,
  isFieldsWithInsideLabel,
  isOneTermsByDefault,
  isSportsPromo,
  regBtnText,
}) => {
  // TODO: refactor
  const { navigate } = useNavigation();
  const { t } = useTranslation('common');
  const close = useModalsStore.use.close();
  const {
    setCurrentPersistentStep,
    getPersistentFormData,
    resetPersistentForm,
  } = usePersistentFormsSlice();
  const initialValues = getPersistentFormData(
    PersistentFormName.PreRegistration
  ) as RegisterForm;

  const isSports = useIsSports();

  const [showPromosCheckboxes, setShowPromosCheckboxes] = useState(
    // biome-ignore lint/complexity/useSimplifiedLogicExpression: must have
    initialValues?.receivePromosMain || false
  );
  const { isLanding } = useIsLanding();
  const { styleScheme, input, select, buttons } = useRegModalTheme();
  const { newTerms, oneTerms } = useAffiliateRegConfig();
  const useCommonStore = useGetCommonStore();
  const resolvedOneTerms = oneTerms || !!isOneTermsByDefault;

  const countryCode = useCommonStore.use.countryCode();

  const { syncUserLPCoupon } = useSyncUserLPCoupon();

  const {
    afterRegistrationRedirectUrl,
    enableCurrencies,
    pendingUser,
    regBannerActionBtnRect,
    fetchUser,
    insertUser,
    initialRegFormCurrency,
    isOneStepRegistrationForm,
    oneStepAfterRegUrl: oneStepAfterRegUrlStore,
    regBanner,
  } = useCommonStore(
    useShallow((state) => ({
      afterRegistrationRedirectUrl: state.afterRegistrationRedirectUrl,
      enableCurrencies: state.enableCurrencies,
      pendingUser: state.pendingUser,
      regBannerActionBtnRect: state.regBannerActionBtnRect,
      fetchUser: state.fetchUser,
      insertUser: state.insertUser,
      initialRegFormCurrency: state.initialRegFormCurrency,
      isOneStepRegistrationForm: state.isOneStepRegistrationForm,
      oneStepAfterRegUrl: state.oneStepAfterRegUrl,
      regBanner: state.regBanner,
    }))
  );

  const isOneStepRegistration =
    isOneStepRegistrationProp ?? isOneStepRegistrationForm;
  const resolvedIsSportsPromo = isSportsPromo ?? regBanner?.isSportsPromo;
  const resolvedOneStepAfterRegUrl =
    oneStepAfterRegUrl ?? oneStepAfterRegUrlStore;

  const { isNDBFlow } = useNDBFlow();

  const open = useModalsStore.use.open();
  const {
    trigger: requestRegister,
    error: registerError,
    isMutating: registerPending,
  } = useSwrMutation<User>({
    key: 'registerKey',
    fetcher: register,
    options: { populateCache: true },
  });
  const { handleLogin, loginPending, userData, loginError } = useLogin();
  const [isAuthRequestsPending, setIsAuthRequestsPending] = useState(false);
  const formApiRef = useRef<CustomFormApi<RegisterForm>>();
  const { validateEmail: validateEmailDomain } = useEmailValidation();
  const justRegisteredRef = useRef(false);

  useEffect(() => {
    return () => {
      // Clear only after redirect
      if (justRegisteredRef.current) {
        resetPersistentForm(PersistentFormName.PreRegistration);
      }
    };
  }, [resetPersistentForm]);

  useErrorValidation({
    initialValues,
    registerError,
    loginError,
    isAuthRequestsPending,
    setIsAuthRequestsPending,
    isOneStepRegistration,
    formApiRef,
  });

  const landingDepositNavigate = useCallback(
    (isCrypto?: boolean) => {
      if (resolvedIsSportsPromo && resolvedOneStepAfterRegUrl) {
        window.location.href = resolvedOneStepAfterRegUrl;

        return;
      }

      const nonNdbFlowRedirect = isCrypto
        ? '/?modal=deposit'
        : '/?modal=registration';

      const redirectUrl = isNDBFlow
        ? '/?modal=ndb-email-confirm'
        : nonNdbFlowRedirect;

      navigate(resolvedOneStepAfterRegUrl ?? redirectUrl, false, true);
    },
    [navigate, isNDBFlow, resolvedOneStepAfterRegUrl, resolvedIsSportsPromo]
  );

  const { syncStag } = useOrganicStag();

  const handleSubmit = useCallback(
    async ({
      termsAcceptanceAgeAcceptance,
      receivePromosMain,
      ...values
    }: RegisterForm) => {
      // Validate email domain before proceeding
      const email = values.email || initialValues.email;
      const isEmailValid = await validateEmailDomain(email);

      if (!isEmailValid) {
        formApiRef.current?.setError(
          'email',
          t('auth.registration.temporaryEmail')
        );
        return;
      }

      setIsAuthRequestsPending(true);

      const loginValues = {
        email: values.email || initialValues.email,
        password: values.password || initialValues.password,
      };

      const { receivePromos, receiveSmsPromos, receivePromosViaPhoneCalls } =
        getPromoConsents({
          oneTerms: resolvedOneTerms,
          newTerms,
          receivePromosMain:
            receivePromosMain ?? initialValues.receivePromosMain,
          receivePromos: values.receivePromos || initialValues.receivePromos,
          receiveSmsPromos:
            values.receiveSmsPromos || initialValues.receiveSmsPromos,
          receivePromosViaPhoneCalls:
            values.receivePromosViaPhoneCalls ||
            initialValues.receivePromosViaPhoneCalls,
        });

      const requestData = {
        ...values,
        ...loginValues,
        receivePromos,
        receiveSmsPromos,
        ageAcceptance:
          termsAcceptanceAgeAcceptance ||
          initialValues.termsAcceptanceAgeAcceptance,
        termsAcceptance:
          termsAcceptanceAgeAcceptance ||
          initialValues.termsAcceptanceAgeAcceptance,
        receivePromosViaPhoneCalls,
        agreedToPartnerPromotions: true,
      };
      try {
        await syncStag();
        const response = await requestRegister(requestData);
        if (response) {
          justRegisteredRef.current = true;

          sendRegFormSubmitEvent({
            status: 'success',
            action: 'registration',
            method: 'email',
          });

          const isCrypto = isCryptoCurrency(
            DEFAULT_CURRENCIES,
            values.currency
          );

          if (!isOneStepRegistration) {
            showTooltip({
              type: TooltipTypes.SUCCESS,
              body: t('auth.registration.success'),
            });
          }

          // isOneStepRegistration is only rendered from landing templates
          if (!isCrypto) {
            setCurrentPersistentStep(
              PersistentFormName.Registration,
              NamesSteps.PERSONAL_INFORMATION
            );
          }

          // TODO: order this function is required until fix additional modal
          await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);
          syncUserLPCoupon();

          if (isSliderShortForm) {
            if (isCrypto) {
              open(Modals.Deposit);
            } else {
              setCurrentPersistentStep(
                PersistentFormName.Registration,
                NamesSteps.PERSONAL_INFORMATION
              );
              open(Modals.Registration);
              showTooltip({
                type: TooltipTypes.SUCCESS,
                body: t('auth.registration.success'),
              });
            }
          }

          if (!isOneStepRegistration && isCrypto) {
            if (afterRegistrationRedirectUrl) {
              return navigate(afterRegistrationRedirectUrl);
            }
            if (isLanding) {
              return landingDepositNavigate(isCrypto);
            }

            return isNDBFlow
              ? open(Modals.NDBEmailConfirm)
              : open(Modals.Deposit);
          }

          if (isOneStepRegistration && isLanding) {
            landingDepositNavigate(isCrypto);
          }
        }
      } catch (e: any) {
        if (e.email?.taken) {
          handleLogin(loginValues);
        } else {
          Sentry.captureMessage(
            `Error in registration request - ${JSON.stringify(e)}`
          );
          setIsAuthRequestsPending(false);
          sendRegFormSubmitEvent({
            status: 'failed',
            action: 'registration',
            method: 'email',
            request_data: requestData,
            error_data: e,
          });

          if (e?.password?.emailEquivalent) {
            return showTooltip({
              type: TooltipTypes.ERROR,
              body: e.password.emailEquivalent,
            });
          }

          showTooltip({
            type: TooltipTypes.ERROR,
            body: t('errorUnknown'),
          });
        }
      } finally {
        if (isSports) {
          refreshSportsUser();
        }
      }
    },
    [
      afterRegistrationRedirectUrl,
      isOneStepRegistration,
      requestRegister,
      fetchUser,
      landingDepositNavigate,
      open,
      t,
      isLanding,
      handleLogin,
      isSports,
      validateEmailDomain,
      initialValues.email,
      initialValues.receivePromosMain,
      resolvedOneTerms,
      newTerms,
      initialValues.password,
      initialValues.receivePromos,
      initialValues.receivePromosViaPhoneCalls,
      initialValues.receiveSmsPromos,
      initialValues.termsAcceptanceAgeAcceptance,
      isNDBFlow,
      isSliderShortForm,
      navigate,
      resetPersistentForm,
      setCurrentPersistentStep,
      syncStag,
      syncUserLPCoupon,
    ]
  );

  const currenciesOptions = useMemo(
    () =>
      enableCurrencies
        .filter(({ isDisabledOnRegistration }) => !isDisabledOnRegistration)
        .map(({ code, symbol, fiat }) => ({
          name: fiat ? code : symbol,
          value: code,
        })),
    [enableCurrencies]
  );

  const { countryOptions, initialCountry } =
    useRegistrationCountryOptions(countryCode);

  const preselectedValues = {
    country: initialCountry,
    currency: getCurrencyCodeByLocation(countryCode),
    termsAcceptanceAgeAcceptance: false,
  };

  const isEventTouchedSent = useRef(false);
  const saveFormValuesOnChange = useCallback(
    ({ touched }: CustomFormState<RegisterForm>) => {
      const isFormTouched = Object.values(touched).some((t) => t);

      if (isFormTouched && !isEventTouchedSent.current) {
        sendRegFormFirstTouchedEvent();
        isEventTouchedSent.current = true;
      }
    },
    []
  );

  const handlePromoCheckboxesOnChange = useCallback(
    (formState: CustomFormState<RegisterForm>) => {
      const receivePromosMainCheckbox = formState.values?.receivePromosMain;

      handleChange && handleChange(formState);

      if (!(newTerms || resolvedOneTerms)) {
        setShowPromosCheckboxes(!!receivePromosMainCheckbox);
      }
    },
    [handleChange, newTerms, resolvedOneTerms]
  );

  useEffect(() => {
    sendRegFormAppearanceEvent();
  }, []);

  useEffect(() => {
    if (userData) {
      insertUser(userData, REASON_FOR_UPDATING_USER.LOGIN);
      setIsAuthRequestsPending(false);
      sendRegFormSubmitEvent({
        status: 'success',
        action: 'login',
        method: 'email',
      });
      close({ useCustomOnClose: true });
      isLanding && navigate('/');
      return;
    }
  }, [userData, insertUser, close, navigate, isLanding]);

  useEffect(() => {
    handleChange &&
      formApiRef.current &&
      handleChange(formApiRef.current?.getFormState());
  }, [handleChange]);

  useEffect(() => {
    if (!showPromosCheckboxes) {
      formApiRef.current?.setValue('receivePromos', false);
      formApiRef.current?.setValue('receiveSmsPromos', false);
      formApiRef.current?.setValue('receivePromosViaPhoneCalls', false);
    }
  }, [showPromosCheckboxes]);

  const isInputWithInsideLabel =
    input.withInsideLabel || isFieldsWithInsideLabel;
  const isSelectWithInsideLabel =
    select.withInsideLabel || select.withoutLabel || isFieldsWithInsideLabel;

  const isSubmitBtnLoading =
    pendingUser || registerPending || loginPending || isAuthRequestsPending;

  return (
    <>
      {!isLanding && (
        <ContinueWithGoogle
          dividerGap="small"
          marginBottom={isOneStepRegistration ? 'small' : 'large'}
          onSendGAEvent={sendRegGoogleAuthButtonClickEvent}
        />
      )}

      <Form<RegisterForm>
        formApiRef={formApiRef}
        id={formId}
        initialValues={{ ...preselectedValues, ...initialValues }}
        name="registration"
        onChange={(formData: CustomFormState<RegisterForm>) => {
          saveFormValuesOnChange(formData);
          handlePromoCheckboxesOnChange(formData);
        }}
        onSubmit={({ values }) => handleSubmit(values)}
      >
        <PersistentFormTracker formName={PersistentFormName.PreRegistration} />
        <ScFormRegistration styleScheme={styleScheme}>
          <FormGroup
            styleScheme={styleScheme}
            title={
              isInputWithInsideLabel ? '' : t('auth.registration.form.email')
            }
          >
            <InputEmail
              styleScheme={styleScheme}
              testId="registration-email-input"
              withInsideLabel={input.withInsideLabel}
            />
          </FormGroup>
          <FormGroup
            styleScheme={styleScheme}
            title={
              isInputWithInsideLabel ? '' : t('auth.registration.form.password')
            }
          >
            <Password
              autoComplete="new-password"
              name="password"
              strengthChecker
              styleScheme={styleScheme}
              testId="registration-password-input"
              validate={validatePassword}
              validateOn="change"
              withInsideLabel={input.withInsideLabel}
            />
          </FormGroup>
          <ScRegistrationFormRow>
            <FormGroup
              minWidth={'0'}
              styleScheme={styleScheme}
              title={
                isSelectWithInsideLabel
                  ? ''
                  : t('common:auth.registration.form.country')
              }
            >
              <Select
                mode={ThemeMode.Light}
                name="country"
                options={countryOptions}
                placeholder={t('common:auth.registration.form.country')}
                styleScheme={styleScheme}
                testId="registration-country-select"
                validate={required}
                withInsideLabel={select.withInsideLabel || select.withoutLabel}
              />
            </FormGroup>
            <FormGroup
              styleScheme={styleScheme}
              title={
                isSelectWithInsideLabel
                  ? ''
                  : t('auth.registration.form.currency')
              }
            >
              <Select
                cols={1}
                initialValue={initialRegFormCurrency || null}
                mode={ThemeMode.Light}
                name="currency"
                openOptionsInPortal={openInnerModalsInPortal}
                options={currenciesOptions}
                placeholder={
                  isSports
                    ? t('auth.registration.form.currency')
                    : t('auth.registration.form.selectCurrency')
                }
                styleScheme={styleScheme}
                testId="registration-currency-select"
                validate={required}
                withInsideLabel={select.withInsideLabel || select.withoutLabel}
              />
            </FormGroup>
          </ScRegistrationFormRow>
          <FormGroup
            marginBottom={8}
            marginTop={styleScheme?.checkbox?.wrapperMargin || 8}
            styleScheme={styleScheme}
          >
            <div className="checkbox-list">
              <RegistrationFormCheckboxList
                formId={formId}
                isOneTermsByDefault={isOneTermsByDefault}
                openInnerModalsInPortal={openInnerModalsInPortal}
                showPromosCheckboxes={showPromosCheckboxes}
              />
              {!isOneStepRegistration && isInputPromoStag && (
                <FormGroup title={t('havePromoCode')}>
                  <RegistrationPromoInput />
                </FormGroup>
              )}
            </div>
          </FormGroup>
        </ScFormRegistration>
        {isOneStepRegistration ? (
          <div
            className="reg-banner-btn"
            ref={(node) => {
              if (!node || regBannerActionBtnRect) return;
              useCommonStore.setState({
                regBannerActionBtnRect: node.getBoundingClientRect(),
              });
            }}
          >
            <Button
              data-testid="registration-one-submit-button"
              isLoading={isSubmitBtnLoading}
              {...oneStepButtonTheme}
              small
              type="submit"
              wide
            >
              {regBtnText || t('button.joinNow')}
            </Button>
            {isLanding && (
              <ContinueWithGoogle
                dividerGap="small"
                marginBottom={isOneStepRegistration ? 'small' : 'large'}
                onSendGAEvent={sendRegGoogleAuthButtonClickEvent}
              />
            )}
          </div>
        ) : (
          <ScRegistrationWrapButtons
            cols={1}
            isSticky={buttons?.regButton?.isRegistrationSticky}
            marginBottom={buttons?.regButton.marginBottom}
          >
            <div className="right-col">
              <Button
                large
                {...buttons?.regButton.colorMode}
                data-testid="registration-submit-button"
                isLoading={isSubmitBtnLoading}
                type="submit"
                wide
              >
                {t(
                  buttons?.regButton?.text || 'auth.registration.buttons.next'
                )}
              </Button>
              {isLanding && (
                <ContinueWithGoogle
                  dividerGap="small"
                  marginBottom={isOneStepRegistration ? 'small' : 'large'}
                  onSendGAEvent={sendRegGoogleAuthButtonClickEvent}
                />
              )}
            </div>
          </ScRegistrationWrapButtons>
        )}
      </Form>
    </>
  );
};
